/* ==========================================================================
   As chamadas à API, tipadas, num lugar só.

   Página não monta URL: se `/contratos` virar `/contratos-ativos` amanhã, o
   conserto é aqui e não em sete telas. E o tipo de retorno documenta a forma da
   resposta melhor que qualquer comentário.
   ========================================================================== */

import client from '@/api/client'
import type {
  Alteracao,
  AnexoParaEnviar,
  Colecao,
  Contrato,
  ContratoAditivo,
  ContratoAlerta,
  ContratoAnexo,
  ContratoItem,
  ContratoSimulacao,
  Empresa,
  Fornecedor,
  FornecedorContato,
  GrupoDePermissoes,
  ListaDePendencias,
  Pagina,
  Papel,
  Resource,
  ResumoDeContratos,
  ResumoDePendencias,
  TipoContrato,
  Unidade,
  UsuarioAdmin,
} from '@/types'

type Params = Record<string, string | number | boolean | undefined>

/** Tira os vazios: `?status=` filtraria por string vazia em vez de não filtrar. */
function limpar(params: Params): Params {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== false),
  )
}

/* ------------------------------------------------------------- pendências */

export const pendencias = {
  async listar(params: Params = {}): Promise<ListaDePendencias> {
    const { data } = await client.get<ListaDePendencias>('/pendencias', {
      params: limpar(params),
    })
    return data
  },

  async resumo(): Promise<ResumoDePendencias> {
    const { data } = await client.get<Resource<ResumoDePendencias>>('/pendencias/resumo')
    return data.data
  },
}

/* --------------------------------------------------------------- contratos */

export const contratos = {
  async listar(params: Params = {}): Promise<Pagina<Contrato>> {
    const { data } = await client.get<Pagina<Contrato>>('/contratos', {
      params: limpar(params),
    })
    return data
  },

  /**
   * Os números do topo, sobre o MESMO conjunto que `listar` devolve.
   *
   * Recebe os mesmos parâmetros de propósito: a tela repassa o objeto de filtros
   * inteiro para as duas chamadas, em vez de escolher o que mandar para cada uma.
   * Escolher é como os dois deixam de falar do mesmo conjunto — os cartões dizendo
   * 42 e a tabela mostrando 37.
   */
  async resumo(params: Params = {}): Promise<ResumoDeContratos> {
    const { data } = await client.get<Resource<ResumoDeContratos>>('/contratos/resumo', {
      params: limpar(params),
    })
    return data.data
  },

  async mostrar(id: number): Promise<Contrato> {
    const { data } = await client.get<Resource<Contrato>>(`/contratos/${id}`)
    return data.data
  },

  /** Cria (sem id) ou edita (com id). A Action do servidor é a mesma nos dois. */
  async salvar(corpo: Record<string, unknown>, id?: number): Promise<Contrato> {
    const { data } = id
      ? await client.put<Resource<Contrato>>(`/contratos/${id}`, corpo)
      : await client.post<Resource<Contrato>>('/contratos', corpo)
    return data.data
  },

  async remover(id: number): Promise<void> {
    await client.delete(`/contratos/${id}`)
  },

  async atualizarStatus(id: number, status: string): Promise<Contrato> {
    const { data } = await client.patch<Resource<Contrato>>(`/contratos/${id}/status`, {
      status,
    })
    return data.data
  },

  async itens(id: number): Promise<ContratoItem[]> {
    // As linhas já vêm no detalhe; este atalho existe para recarregar só elas
    // depois de uma edição, sem repuxar o contrato inteiro.
    const contrato = await contratos.mostrar(id)
    return contrato.itens ?? []
  },

  async historico(id: number, pagina = 1): Promise<Pagina<Alteracao>> {
    const { data } = await client.get<Pagina<Alteracao>>(`/contratos/${id}/historico`, {
      params: { page: pagina },
    })
    return data
  },

  async aditivos(id: number): Promise<ContratoAditivo[]> {
    const { data } = await client.get<Colecao<ContratoAditivo>>(
      `/contratos/${id}/aditivos`,
    )
    return data.data
  },

  async anexos(id: number): Promise<ContratoAnexo[]> {
    const { data } = await client.get<Colecao<ContratoAnexo>>(`/contratos/${id}/anexos`)
    return data.data
  },

  /**
   * Envia um anexo: arquivo OU link externo, nunca os dois.
   *
   * Cada campo só entra no FormData quando tem valor, e isso não é economia: a
   * validação usa `prohibits` dos dois lados, então mandar `url: ''` junto com o
   * arquivo devolveria 422 dizendo "envie o arquivo ou o link, não os dois" —
   * com o usuário tendo escolhido apenas um. Para o FormData, string vazia é
   * presença.
   *
   * O Content-Type NÃO é definido aqui, de propósito. O axios monta o
   * `multipart/form-data` com o boundary a partir do FormData; declarar o header
   * à mão produz um corpo sem boundary, que o PHP lê como `$_FILES` vazio — e o
   * erro que chega é "o campo arquivo é obrigatório", apontando para o lugar
   * errado.
   */
  async enviarAnexo(id: number, anexo: AnexoParaEnviar): Promise<ContratoAnexo> {
    const corpo = new FormData()

    if (anexo.arquivo) corpo.append('arquivo', anexo.arquivo)
    if (anexo.url) corpo.append('url', anexo.url)
    if (anexo.nome) corpo.append('nome', anexo.nome)
    if (anexo.descricao) corpo.append('descricao', anexo.descricao)

    const { data } = await client.post<Resource<ContratoAnexo>>(
      `/contratos/${id}/anexos`,
      corpo,
    )
    return data.data
  },

  /**
   * Só nome e descrição se editam.
   *
   * Trocar o arquivo de um anexo existente apagaria prova sem deixar rastro:
   * quem consultasse o histórico veria o mesmo registro apontando para outro
   * documento. Trocar é remover e enviar de novo.
   */
  async renomearAnexo(
    contratoId: number,
    anexoId: number,
    corpo: { nome: string; descricao: string | null },
  ): Promise<ContratoAnexo> {
    const { data } = await client.patch<Resource<ContratoAnexo>>(
      `/contratos/${contratoId}/anexos/${anexoId}`,
      corpo,
    )
    return data.data
  },

  async removerAnexo(contratoId: number, anexoId: number): Promise<void> {
    await client.delete(`/contratos/${contratoId}/anexos/${anexoId}`)
  },

  async alertas(id: number, apenasAbertos = false): Promise<ContratoAlerta[]> {
    const { data } = await client.get<Colecao<ContratoAlerta>>(
      `/contratos/${id}/alertas`,
      {
        params: limpar({ abertos: apenasAbertos }),
      },
    )
    return data.data
  },

  async resolverAlerta(
    contratoId: number,
    alertaId: number,
    resolvido: boolean,
  ): Promise<ContratoAlerta> {
    const { data } = await client.patch<Resource<ContratoAlerta>>(
      `/contratos/${contratoId}/alertas/${alertaId}/resolucao`,
      { resolvido },
    )
    return data.data
  },

  async simulacoes(id: number): Promise<ContratoSimulacao[]> {
    const { data } = await client.get<Colecao<ContratoSimulacao>>(
      `/contratos/${id}/simulacoes`,
    )
    return data.data
  },

  /** Calcula sem gravar — é o que a tela chama enquanto a pessoa mexe nos campos. */
  async previa(id: number, params: Params = {}): Promise<ContratoSimulacao> {
    const { data } = await client.get<Resource<ContratoSimulacao>>(
      `/contratos/${id}/simulacoes/previa`,
      { params: limpar(params) },
    )
    return data.data
  },

  /** Calcula E grava, atribuído a uma pessoa. */
  async simular(id: number, corpo: Params = {}): Promise<ContratoSimulacao> {
    const { data } = await client.post<Resource<ContratoSimulacao>>(
      `/contratos/${id}/simulacoes`,
      limpar(corpo),
    )
    return data.data
  },
}

export const tiposDeContrato = {
  async listar(): Promise<TipoContrato[]> {
    const { data } = await client.get<Colecao<TipoContrato>>('/tipos-contrato')
    return data.data
  },
}

/* ------------------------------------------------------------ fornecedores */

export const fornecedores = {
  async listar(params: Params = {}): Promise<Pagina<Fornecedor>> {
    const { data } = await client.get<Pagina<Fornecedor>>('/fornecedores', {
      params: limpar(params),
    })
    return data
  },

  async mostrar(id: number): Promise<Fornecedor> {
    const { data } = await client.get<Resource<Fornecedor>>(`/fornecedores/${id}`)
    return data.data
  },

  async salvar(corpo: Record<string, unknown>, id?: number): Promise<Fornecedor> {
    const { data } = id
      ? await client.put<Resource<Fornecedor>>(`/fornecedores/${id}`, corpo)
      : await client.post<Resource<Fornecedor>>('/fornecedores', corpo)
    return data.data
  },

  async salvarContato(
    fornecedorId: number,
    corpo: Record<string, unknown>,
    contatoId?: number,
  ): Promise<FornecedorContato> {
    const url = `/fornecedores/${fornecedorId}/contatos`
    const { data } = contatoId
      ? await client.put<Resource<FornecedorContato>>(`${url}/${contatoId}`, corpo)
      : await client.post<Resource<FornecedorContato>>(url, corpo)
    return data.data
  },

  async removerContato(fornecedorId: number, contatoId: number): Promise<void> {
    await client.delete(`/fornecedores/${fornecedorId}/contatos/${contatoId}`)
  },

  async historico(id: number, pagina = 1): Promise<Pagina<Alteracao>> {
    const { data } = await client.get<Pagina<Alteracao>>(
      `/fornecedores/${id}/historico`,
      {
        params: { page: pagina },
      },
    )
    return data
  },

  /**
   * Verifica a situação na Receita. Nunca bloqueia: a API responde 200 mesmo
   * quando a consulta falha, dizendo que não conseguiu.
   */
  async verificarSituacao(id: number): Promise<Fornecedor> {
    const { data } = await client.post<Resource<Fornecedor>>(
      `/fornecedores/${id}/verificar-situacao`,
    )
    return data.data
  },
}

/* ----------------------------------------------------------- administração */

export const usuarios = {
  async listar(params: Params = {}): Promise<Pagina<UsuarioAdmin>> {
    const { data } = await client.get<Pagina<UsuarioAdmin>>('/usuarios', {
      params: limpar(params),
    })
    return data
  },

  async mostrar(id: number): Promise<UsuarioAdmin> {
    const { data } = await client.get<Resource<UsuarioAdmin>>(`/usuarios/${id}`)
    return data.data
  },

  async criar(corpo: Record<string, unknown>): Promise<UsuarioAdmin> {
    const { data } = await client.post<Resource<UsuarioAdmin>>('/usuarios', corpo)
    return data.data
  },

  async atualizar(id: number, corpo: Record<string, unknown>): Promise<UsuarioAdmin> {
    const { data } = await client.put<Resource<UsuarioAdmin>>(`/usuarios/${id}`, corpo)
    return data.data
  },

  async redefinirSenha(id: number, password: string): Promise<void> {
    await client.put(`/usuarios/${id}/senha`, { password })
  },

  async alternarAtivo(id: number, ativo: boolean): Promise<UsuarioAdmin> {
    const { data } = await client.patch<Resource<UsuarioAdmin>>(`/usuarios/${id}/ativo`, {
      ativo,
    })
    return data.data
  },

  async definirAcessos(
    id: number,
    empresas: { empresa_id: number; todas_unidades: boolean; unidades?: number[] }[],
  ): Promise<UsuarioAdmin> {
    const { data } = await client.put<Resource<UsuarioAdmin>>(`/usuarios/${id}/acessos`, {
      empresas,
    })
    return data.data
  },
}

export const papeis = {
  async listar(): Promise<Papel[]> {
    const { data } = await client.get<Colecao<Papel>>('/papeis')
    return data.data
  },

  async salvar(corpo: Record<string, unknown>, id?: number): Promise<Papel> {
    const { data } = id
      ? await client.put<Resource<Papel>>(`/papeis/${id}`, corpo)
      : await client.post<Resource<Papel>>('/papeis', corpo)
    return data.data
  },

  async remover(id: number): Promise<void> {
    await client.delete(`/papeis/${id}`)
  },
}

export const permissoes = {
  async catalogo(): Promise<GrupoDePermissoes[]> {
    const { data } = await client.get<Colecao<GrupoDePermissoes>>('/permissoes')
    return data.data
  },
}

export const organizacao = {
  async empresas(): Promise<Empresa[]> {
    const { data } = await client.get<Colecao<Empresa>>('/empresas')
    return data.data
  },

  async atualizarEmpresa(id: number, corpo: Record<string, unknown>): Promise<Empresa> {
    const { data } = await client.put<Resource<Empresa>>(`/empresas/${id}`, corpo)
    return data.data
  },

  async unidades(): Promise<Unidade[]> {
    const { data } = await client.get<Colecao<Unidade>>('/unidades')
    return data.data
  },

  async salvarUnidade(corpo: Record<string, unknown>, id?: number): Promise<Unidade> {
    const { data } = id
      ? await client.put<Resource<Unidade>>(`/unidades/${id}`, corpo)
      : await client.post<Resource<Unidade>>('/unidades', corpo)
    return data.data
  },
}
